import type { ClientOptions } from '../schemas/client';
import type { APIRequestInit } from '../types/api';

import { EvolutionApiError, extractErrorMessage } from './errors';

export class ApiService {
  constructor(private options: ClientOptions) {}

  setInstance(instance: string) {
    this.options.instance = instance;
  }

  async request<T = unknown>(path: string, options: APIRequestInit = {}): Promise<T> {
    // Validate input parameters
    if (!path || typeof path !== 'string' || path?.trim()?.length === 0) {
      throw new EvolutionApiError('Invalid path parameter', {
        message: 'Path must be a non-empty string',
        path,
      });
    }

    if (!this.options.serverUrl || typeof this.options.serverUrl !== 'string') {
      throw new EvolutionApiError('Invalid server URL configuration', {
        message: 'Server URL must be configured and be a valid string',
        serverUrl: this.options.serverUrl,
      });
    }

    const { isInstanceUrl = true } = options;
    let instance = this.options.instance;

    // Priority: options.instance > this.options.instance
    if (options.instance) {
      instance = options.instance;
      delete options.instance;
    }

    if (isInstanceUrl && !instance) {
      throw new EvolutionApiError('Instance not set', {
        message:
          'Please set the instance before making a request or pass instance in the method options.',
        path,
        isInstanceUrl,
      });
    }

    const { init, params } = this.makeInit(options);

    const urlPath = isInstanceUrl ? `/${path}/${instance}/` : `/${path}/`;
    const url = new URL(urlPath, this.options.serverUrl);

    // Add query parameters if any
    if (params.toString()) {
      url.search = params.toString();
    }

    // Generate and store curl command for debugging
    const curlCommand = this.generateCurlCommand(
      init.method || 'GET',
      url.toString(),
      init.headers,
      init.body as string,
    );

    // Print curl command before making the request
    if (this.options.debug) {
      console.log('\n=== CURL COMMAND ===');
      console.log(curlCommand);
      console.log('===================\n');
    }

    let response: Response;
    let data: any;
    let responseText: string = '';

    try {
      response = await fetch(url, init);
      responseText = await response.text();

      // Only parse as JSON if there's content
      data = responseText ? JSON.parse(responseText) : null;
    } catch (error) {
      if (this.options.debug) {
        console.log(error);

        // Print curl command again in case of error for easier debugging
        console.log('\n=== CURL COMMAND (ERROR) ===');
        console.log(curlCommand);
        console.log('============================\n');
      }

      // Handle network errors or JSON parsing errors
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        throw new EvolutionApiError('Failed to parse response as JSON', {
          originalError: error,
          responseText: responseText || 'Empty response',
          url: url.toString(),
          status: response?.status,
        });
      }
      throw new EvolutionApiError('Network or parsing error', {
        originalError: error,
        url: url.toString(),
        responseText: responseText || 'No response',
      });
    }

    // Print API response in JSON format
    if (this.options.debug) {
      console.log('\n=== API RESPONSE ===');
      console.log(JSON.stringify(data, null, 2));
      console.log('===================\n');
    }

    if (!response.ok) {
      // Print curl command again in case of HTTP error for easier debugging
      if (this.options.debug) {
        console.log('\n=== CURL COMMAND (HTTP ERROR) ===');
        console.log(curlCommand);
        console.log('================================\n');
      }

      // Extract meaningful error message from the response
      const errorMessage =
        extractErrorMessage(data) ||
        `Request failed with status ${response.status}: ${response.statusText}`;

      throw new EvolutionApiError(
        errorMessage,
        {
          message: errorMessage,
          response: data || {},
          url: url.toString(),
          params: params.toString(),
          body: options.body || {},
        },
        response.status,
      );
    }

    return data;
  }

  private makeInit(options: APIRequestInit): {
    init: RequestInit;
    params: URLSearchParams;
  } {
    const init: RequestInit = {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        apikey: this.options.token, // Evolution API uses apikey header
        ...this.options.headers,
        ...options.headers,
      },
    };

    if (options.body) {
      init.body = JSON.stringify(options.body);
    }

    const params = new URLSearchParams();
    if (options.params) {
      // Convert any object to URLSearchParams
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    return { init, params };
  }

  private generateCurlCommand(
    method: string,
    url: string,
    headers: Record<string, string> | HeadersInit,
    body?: BodyInit,
  ): string {
    const curlParts = ['curl'];

    // Add method
    if (method !== 'GET') {
      curlParts.push(`-X ${method}`);
    }

    // Add headers
    let headerEntries: [string, string][];

    if (headers instanceof Headers) {
      const entries: [string, string][] = [];
      headers.forEach((value, key) => {
        entries.push([key, value]);
      });
      headerEntries = entries;
    } else {
      headerEntries = Object.entries(headers as Record<string, string>);
    }

    headerEntries.forEach(([key, value]) => {
      // Skip logging the apikey header for security
      if (key.toLowerCase() !== 'apikey') {
        curlParts.push(`-H '${key}: ${value}'`);
      } else {
        curlParts.push(`-H '${key}: [REDACTED]'`);
      }
    });

    // Add body if present
    if (body) {
      const bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
      curlParts.push(`-d '${bodyStr}'`);
    }

    // Add URL
    curlParts.push(`'${url}'`);

    return curlParts.join(' \\\n  ');
  }

  async get<T = unknown>(path: string, options: Omit<APIRequestInit, 'method'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  async post<T = unknown>(path: string, options: Omit<APIRequestInit, 'method'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST' });
  }

  async put<T = unknown>(path: string, options: Omit<APIRequestInit, 'method'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PUT' });
  }

  async patch<T = unknown>(path: string, options: Omit<APIRequestInit, 'method'> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH' });
  }

  async delete<T = unknown>(
    path: string,
    options: Omit<APIRequestInit, 'method'> = {},
  ): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }
}
